import type { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useRef } from 'react';
import ButtonDropdown from 'src/components/button-dropdown';

export type ActionItem = {
  text: string;
  id: string;
  disabled?: boolean;
  disabledReason?: string;
  onItemClick: () => Promise<void> | void;
  iconSvg?: ReactNode;
};

export type Props = {
  buttonText: string;
  items: ActionItem[];
  variant?: 'normal' | 'primary';
  testId?: string;
  disabled?: boolean;
  /**
   * Fired on a trigger gesture that opens the dropdown — mousedown, or Enter /
   * Space / ArrowDown. Opt-in: ButtonDropdown exposes no open-state callback,
   * so this is detected with capture-phase handlers on a wrapping span and
   * reflects open *intent*, not a verified closed→open transition. Events from
   * the expanded menu (`[role="menu"]`) are ignored so it fires only for the
   * trigger, not for menu interactions.
   */
  onOpen?: () => void;
  /**
   * Fired when the pointer enters a disabled menu item — the moment its
   * `disabledReason` tooltip shows. Receives the item id. Detected with a
   * capture-phase mouseover on the wrapping span (same caveats as `onOpen`)
   * and deduped per item, so resting on an item fires once.
   */
  onDisabledItemHover?: (itemId: string) => void;
};

const isFromMenu = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[role="menu"]') !== null;

const ActionsButton: FC<Props> = ({
  buttonText,
  items,
  variant,
  testId,
  disabled = false,
  onOpen,
  onDisabledItemHover,
}) => {
  const lastHoveredDisabledItem = useRef<Element | null>(null);
  const dropdown = (
    <ButtonDropdown
      disabled={disabled}
      data-testid={testId}
      items={items}
      variant={variant || 'normal'}
      onItemClick={(event) => {
        items.find((option) => option.id === event.detail.id)?.onItemClick();
      }}
    >
      {buttonText}
    </ButtonDropdown>
  );

  // Nothing to fire, or disabled (can't open) — render the bare dropdown
  // unchanged; only wrap with the capture handlers when there's a listener.
  if ((!onOpen && !onDisabledItemHover) || disabled) {
    return dropdown;
  }

  const handleMouseDownCapture = (event: MouseEvent<HTMLSpanElement>) => {
    if (!onOpen || isFromMenu(event.target)) {
      return;
    }
    onOpen();
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (
      !onOpen ||
      (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown')
    ) {
      return;
    }
    if (isFromMenu(event.target)) {
      return;
    }
    onOpen();
  };

  const handleMouseOverCapture = (event: MouseEvent<HTMLSpanElement>) => {
    if (!onDisabledItemHover || !(event.target instanceof Element)) {
      return;
    }
    const menuItem = event.target.closest(
      '[role="menuitem"][aria-disabled="true"]'
    );
    if (!menuItem) {
      lastHoveredDisabledItem.current = null;

      return;
    }
    if (menuItem === lastHoveredDisabledItem.current) {
      return;
    }
    lastHoveredDisabledItem.current = menuItem;
    // The menuitem's presentation <li> carries the item id as data-testid
    // (Cloudscape internal). Resolve it back to a source `items` entry so the
    // emitted key is one we own — never the locale-dependent visible label —
    // and stays `'unknown'` if the DOM structure ever drifts.
    const testId = menuItem
      .closest('[data-testid]')
      ?.getAttribute('data-testid');
    const matchedItem = items.find((item) => item.id === testId);
    onDisabledItemHover(matchedItem?.id ?? 'unknown');
  };

  return (
    <span
      onMouseDownCapture={handleMouseDownCapture}
      onKeyDownCapture={handleKeyDownCapture}
      onMouseOverCapture={handleMouseOverCapture}
    >
      {dropdown}
    </span>
  );
};

export default ActionsButton;
